import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
	Allergies,
	MedicalConditions,
	Medications,
	MenHealth,
	Screenings,
	SurgicalHistory,
	Vaccinations,
	WomenHealth,
} from 'src/app/beans/health-history/medical-history.bean';
import { Config } from 'src/app/config/config';
import { Environment } from 'src/app/config/environment';
import { METHOD_TYPE } from 'src/app/constants/service.constants';
import { ServiceRouter } from '../core/http-service-router.service';
import { JwtHelper } from '../core/jwt-helper.service';
import { HAFamilyHistoryDTO } from 'src/app/modules/shared/types';

/**
 * contains api calls related to medical history section of health history
 */
@Injectable()
export class MedicalHistoryService {
	/**
	 * constructor
	 * @param environment
	 * @param serviceRouter
	 * @param config
	 */
	constructor(
		private jwtService: JwtHelper,
		private environment: Environment,
		private serviceRouter: ServiceRouter,
		private config: Config
	) {}

	/**
	 * Gets past present health conditions
	 */
	getPastPresentMedicalConditions(apptID, haID): Observable<any> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.pastPresentMedicalConditions}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * Gets surgical history values
	 */
	getSurgicalHistory(apptID, haID): Observable<any> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.surgicalHistory}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * Gets medication values
	 */
	getMedications(apptID, haID): Observable<any> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.medications}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * Gets allergies values
	 */
	getAllergies(apptID, haID): Observable<any> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.allergies}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * Gets screenings values
	 */
	getScreenings(apptID, haID): Observable<any> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.screenings}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * Gets vaccinations values
	 */
	getVaccinations(apptID, haID): Observable<any> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.vaccinations}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves past present medical conditions
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	savePastPresentMedicalConditions(apptID, haID, data: MedicalConditions): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.pastPresentMedicalConditions}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves surgical history
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	saveSurgicalHistory(apptID, haID, data: SurgicalHistory): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.surgicalHistory}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves medications
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	saveMedications(apptID, haID, data: Medications): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.medications}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves allergies
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	saveAllergies(apptID, haID, data: Allergies): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.allergies}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves screenings
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	saveScreenings(apptID, haID, data: Screenings): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.screenings}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves vaccinations
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	saveVaccinations(apptID, haID, data: Vaccinations): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.vaccinations}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * Gets men health values
	 */
	getMenHealth(apptID, haID): Observable<any> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.menHealth}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves men health
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	saveMenHealth(apptID, haID, data: MenHealth): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.menHealth}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * Gets women health values
	 */
	getWomenHealth(apptID, haID): Observable<any> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.womenHealth}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves women health
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	saveWomenHealth(apptID, haID, data: WomenHealth): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.womenHealth}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * Gets women health values
	 */
	getFamilyHistory(apptID: number, haID: number): Observable<{ data: HAFamilyHistoryDTO }> {
		if (!haID || haID == 0) {
			return new Observable(observer => {
				observer.next({ data: {} as HAFamilyHistoryDTO });
				observer.complete();
			});
		}
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.familyHistory}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_GET,
			'',
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}

	/**
	 * saves women health
	 * @param apptID
	 * @param haID
	 * @param data
	 */
	saveFamilyHistory(apptID: number, haID: number, data: HAFamilyHistoryDTO): Observable<any> {
		const epmsPID = this.jwtService.getEpmsId();
		return this.serviceRouter.makeRequest(
			this.environment.getEnvironment().HA_BASE_URL,
			`${this.config.url.newHealthAssessmentApi.familyHistory}?epmsPID=${epmsPID}&apptID=${apptID}&healthAssessmentID=${haID}`,
			METHOD_TYPE.SECURED_POST,
			data,
			this.environment.getEnvironment().HA_BASE_DOMAIN
		);
	}
}
